import React, { useState } from 'react';
import './StoreLogin.css';
import storeService from '../services/storeService';

interface StoreLoginProps {
  onLogin: (token: string, store: any) => void;
}

const StoreLogin: React.FC<StoreLoginProps> = ({ onLogin }) => {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [registerData, setRegisterData] = useState({
    storeName: '',
    ownerName: '',
    address: '',
    phone: '',
    email: '',
    pincode: '',
    category: ''
  });

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    // For store login, skip OTP sending and go directly to login screen
    setOtpSent(true);
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await storeService.storeLogin(phone, otp);
      
      if (response.token && response.store) {
        onLogin(response.token, response.store);
      } else {
        setError('Store not found. Please contact admin.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid OTP or store not registered');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    
    try {
      await storeService.sendOTP(phone);
      alert('OTP resent successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (registerData.phone.length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const storePayload = {
        name: registerData.storeName,
        ownerName: registerData.ownerName,
        category: registerData.category,
        location: {
          address: registerData.address,
          city: 'Bhadrachalam',
          state: 'Telangana',
          pincode: registerData.pincode,
          coordinates: {
            latitude: 17.6688,
            longitude: 80.8936
          }
        },
        phone: registerData.phone,
        email: registerData.email,
        isActive: false, // Pending admin approval
        isOpen: false
      };

      await storeService.registerStore(storePayload);
      alert('Registration successful! Your store is pending admin approval. You will be able to login once approved.');
      setShowRegisterForm(false);
      setRegisterData({
        storeName: '',
        ownerName: '',
        address: '',
        phone: '',
        email: '',
        pincode: '',
        category: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Store may already exist.');
    } finally {
      setLoading(false);
    }
  };

  if (showRegisterForm) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="logo-wrapper">
              <img src="/logo-new.png" alt="BringIt" className="login-logo" />
            </div>
            <h1 className="login-title">BringIt</h1>
            <p className="login-subtitle">New Store Registration</p>
          </div>

          <form onSubmit={handleRegister} className="login-form">
            <div className="form-group">
              <label htmlFor="storeName">Store Name *</label>
              <input
                type="text"
                id="storeName"
                value={registerData.storeName}
                onChange={(e) => setRegisterData({ ...registerData, storeName: e.target.value })}
                placeholder="Enter your store name"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="ownerName">Owner Name *</label>
              <input
                type="text"
                id="ownerName"
                value={registerData.ownerName}
                onChange={(e) => setRegisterData({ ...registerData, ownerName: e.target.value })}
                placeholder="Enter owner name"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Store Category *</label>
              <select
                id="category"
                value={registerData.category}
                onChange={(e) => setRegisterData({ ...registerData, category: e.target.value })}
                className="form-input"
                required
                style={{ color: registerData.category ? '#333' : '#999' }}
              >
                <option value="" disabled>Select Store Category</option>
                <option value="grocery">🛒 Grocery Store</option>
                <option value="pharmacy">💊 Pharmacy</option>
                <option value="vegetables">🥬 Vegetables & Fruits</option>
                <option value="meat">🥩 Meat & Seafood</option>
                <option value="bakery">🍞 Bakery Items</option>
                <option value="dairy">🥛 Dairy Products</option>
                <option value="restaurant">🍽️ Restaurant</option>
                <option value="sweets">🍬 Sweets & Confectionery</option>
                <option value="beverages">🥤 Beverages</option>
                <option value="petcare">🐾 Pet Care</option>
                <option value="stationery">📚 Stationery</option>
                <option value="flowers">🌸 Flowers & Gifts</option>
                <option value="electronics">📱 Electronics</option>
                <option value="hardware">🔧 Hardware</option>
                <option value="general">🏪 General Store</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="address">Store Address *</label>
              <input
                type="text"
                id="address"
                value={registerData.address}
                onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                placeholder="Enter complete address"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="pincode">Pincode</label>
              <input
                type="text"
                id="pincode"
                value={registerData.pincode}
                onChange={(e) => setRegisterData({ ...registerData, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder="Enter pincode"
                maxLength={6}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="registerPhone">Phone Number *</label>
              <input
                type="tel"
                id="registerPhone"
                value={registerData.phone}
                onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                placeholder="Enter 10-digit phone number"
                maxLength={10}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                placeholder="Enter email (optional)"
                className="form-input"
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading || !registerData.storeName || !registerData.ownerName || !registerData.category || !registerData.address || registerData.phone.length !== 10}
            >
              {loading ? 'Registering...' : 'Register Store'}
            </button>

            <button 
              type="button"
              onClick={() => {
                setShowRegisterForm(false);
                setError('');
              }}
              className="resend-btn"
            >
              ← Back to Login
            </button>

            <p className="info-text">
              ⏳ Your registration will be reviewed by admin. You'll be able to login once approved.
            </p>
          </form>

          <div className="login-footer">
            <p>Store Partner Portal</p>
            <p>Join BringIt delivery network</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo-wrapper">
            <img src="/logo-new.png" alt="BringIt" className="login-logo" />
          </div>
          <h1 className="login-title">BringIt</h1>
          <p className="login-subtitle">Store Partner Login</p>
        </div>

        {!otpSent ? (
          <form onSubmit={handleSendOTP} className="login-form">
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="Enter 10-digit phone number"
                maxLength={10}
                className="form-input"
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading || phone.length !== 10}
            >
              {loading ? 'Sending...' : 'Send OTP'}
            </button>

            <p className="info-text">
              🏪 Only registered stores can login. Contact admin for registration.
            </p>
            <p className="demo-info">
              🔑 Demo OTP: <strong>123456</strong> (for testing)
            </p>

            <div style={{ textAlign: 'center', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', marginBottom: '10px' }}>
                Don't have an account?
              </p>
              <button 
                type="button"
                onClick={() => setShowRegisterForm(true)}
                className="resend-btn"
              >
                📝 Register New Store
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="login-form">
            <div className="phone-display">
              <span>📱 {phone}</span>
              <button 
                type="button" 
                onClick={() => {
                  setOtpSent(false);
                  setOtp('');
                  setError('');
                }}
                className="change-phone-btn"
              >
                Change
              </button>
            </div>

            <div className="form-group">
              <label htmlFor="otp">Enter OTP</label>
              <input
                type="tel"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                className="form-input otp-input"
                autoFocus
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading || otp.length !== 6}
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>

            <button 
              type="button" 
              onClick={handleResendOTP}
              className="resend-btn"
              disabled={loading}
            >
              Resend OTP
            </button>

            <p className="demo-info">
              🔑 Demo OTP: <strong>123456</strong>
            </p>
          </form>
        )}

        <div className="login-footer">
          <p>Store Partner Portal</p>
          <p>Manage orders, menu & earnings</p>
        </div>
      </div>
    </div>
  );
};

export default StoreLogin;
